package dev.wyns.ventrova.ui.android

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppEnvironment
import dev.wyns.ventrova.app.AppViewModel
import dev.wyns.ventrova.app.SuggestionForm
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.app_name
import dev.wyns.ventrova.resources.filter_how_plant_based
import dev.wyns.ventrova.resources.filter_kind_of_place
import dev.wyns.ventrova.resources.ic_check
import dev.wyns.ventrova.resources.ic_close
import dev.wyns.ventrova.resources.ic_error
import dev.wyns.ventrova.resources.ic_my_location
import dev.wyns.ventrova.resources.onboarding_skip
import dev.wyns.ventrova.resources.onboarding_tagline
import dev.wyns.ventrova.resources.onboarding_use_location
import dev.wyns.ventrova.resources.suggest_address
import dev.wyns.ventrova.resources.suggest_address_placeholder
import dev.wyns.ventrova.resources.suggest_back_to_map
import dev.wyns.ventrova.resources.suggest_error
import dev.wyns.ventrova.resources.suggest_name
import dev.wyns.ventrova.resources.suggest_name_placeholder
import dev.wyns.ventrova.resources.suggest_note_placeholder
import dev.wyns.ventrova.resources.suggest_place
import dev.wyns.ventrova.resources.suggest_send
import dev.wyns.ventrova.resources.suggest_thanks
import dev.wyns.ventrova.resources.suggest_thanks_body_short
import dev.wyns.ventrova.resources.suggest_what_is_vegan
import dev.wyns.ventrova.ui.components.LevelGlyph
import dev.wyns.ventrova.ui.components.LevelLegend
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.description
import dev.wyns.ventrova.ui.theme.displayStyle
import dev.wyns.ventrova.ui.theme.filledIcon
import dev.wyns.ventrova.ui.theme.icon
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.palette
import org.jetbrains.compose.resources.stringResource

/** Full-screen dialog to suggest a place, as in the Android design. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun AndroidSuggestDialog(form: SuggestionForm, viewModel: AppViewModel, onDismiss: () -> Unit) {
    Box(Modifier.fillMaxSize().background(VentrovaColors.Canvas).clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {}) {
        Column(Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding().imePadding()) {
            Row(Modifier.fillMaxWidth().height(64.dp).padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                IconCircle(Res.drawable.ic_close, onDismiss)
                Text(stringResource(Res.string.suggest_place), style = bodyStyle(22.sp), modifier = Modifier.weight(1f))
                if (!form.sent) {
                    Surface(
                        onClick = viewModel::sendSuggestion,
                        shape = RoundedCornerShape(20.dp),
                        color = if (form.canSend) VentrovaColors.Accent else Color(0xFFE3E1DA),
                        contentColor = if (form.canSend) Color.White else VentrovaColors.Subtle,
                        modifier = Modifier.padding(end = 8.dp).height(40.dp),
                    ) {
                        Box(Modifier.padding(horizontal = 18.dp), contentAlignment = Alignment.Center) {
                            Text(stringResource(Res.string.suggest_send), style = bodyStyle(14.5.sp, FontWeight.SemiBold, androidx.compose.material3.LocalContentColor.current))
                        }
                    }
                }
            }
            if (form.sent) {
                SuggestThanks(form.name, onDismiss)
                return@Column
            }
            Column(Modifier.weight(1f).verticalScroll(rememberScrollState()).padding(start = 20.dp, end = 20.dp, top = 8.dp, bottom = 40.dp)) {
                OutlinedTextField(
                    value = form.name,
                    onValueChange = { value -> viewModel.updateSuggestion { it.copy(name = value) } },
                    label = { Text(stringResource(Res.string.suggest_name)) },
                    placeholder = { Text(stringResource(Res.string.suggest_name_placeholder)) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = form.address,
                    onValueChange = { value -> viewModel.updateSuggestion { it.copy(address = value) } },
                    label = { Text(stringResource(Res.string.suggest_address)) },
                    placeholder = { Text(stringResource(Res.string.suggest_address_placeholder)) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
                )
                SuggestSection(stringResource(Res.string.filter_kind_of_place))
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    PlaceCategory.entries.forEach { category ->
                        val active = form.category == category
                        FilterChipBox(active = active, onClick = { viewModel.updateSuggestion { it.copy(category = category) } }, inactiveBackground = Color.Transparent, elevated = false) {
                            VIcon(if (active) Res.drawable.ic_check else category.icon, size = 18.dp)
                            Text(stringResource(category.label), style = bodyStyle(14.sp, FontWeight.Medium))
                        }
                    }
                }
                SuggestSection(stringResource(Res.string.filter_how_plant_based))
                VeganLevel.entries.forEach { level ->
                    Row(
                        Modifier.fillMaxWidth().clickable { viewModel.updateSuggestion { it.copy(level = level) } }.padding(vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        RadioButton(selected = form.level == level, onClick = { viewModel.updateSuggestion { it.copy(level = level) } })
                        LevelGlyph(level, size = 14.dp, borderWidth = 2.5.dp)
                        Column(Modifier.weight(1f)) {
                            Text(stringResource(level.label), style = bodyStyle(16.sp))
                            Text(stringResource(level.description), style = bodyStyle(13.5.sp, color = VentrovaColors.Secondary))
                        }
                    }
                }
                OutlinedTextField(
                    value = form.note,
                    onValueChange = { value -> viewModel.updateSuggestion { it.copy(note = value) } },
                    label = { Text(stringResource(Res.string.suggest_what_is_vegan)) },
                    placeholder = { Text(stringResource(Res.string.suggest_note_placeholder)) },
                    minLines = 3,
                    modifier = Modifier.fillMaxWidth().padding(top = 18.dp),
                )
                if (form.showError) {
                    Row(Modifier.padding(top = 10.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        VIcon(Res.drawable.ic_error, size = 18.dp, tint = VentrovaColors.ErrorAndroid)
                        Text(stringResource(Res.string.suggest_error), style = bodyStyle(13.5.sp, color = VentrovaColors.ErrorAndroid))
                    }
                }
            }
        }
    }
}

@Composable
private fun SuggestSection(text: String) {
    Text(text, style = bodyStyle(14.sp, FontWeight.SemiBold, VentrovaColors.AccentDark), modifier = Modifier.padding(top = 24.dp, bottom = 10.dp))
}

@Composable
private fun SuggestThanks(name: String, onDone: () -> Unit) {
    Column(
        Modifier.fillMaxSize().padding(start = 40.dp, end = 40.dp, bottom = 80.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Box(
            Modifier.size(88.dp).rotate(-8f).background(VentrovaColors.AccentContainer, RoundedCornerShape(30.dp)),
            contentAlignment = Alignment.Center,
        ) {
            VIcon(Res.drawable.ic_check, size = 44.dp, tint = VentrovaColors.OnAccentContainerAlt)
        }
        Text(stringResource(Res.string.suggest_thanks), style = displayStyle(26), modifier = Modifier.padding(top = 24.dp))
        Text(
            stringResource(Res.string.suggest_thanks_body_short, name.trim()),
            style = bodyStyle(16.sp, color = VentrovaColors.Secondary),
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(top = 8.dp),
        )
        Surface(onClick = onDone, shape = RoundedCornerShape(24.dp), color = VentrovaColors.Accent, contentColor = Color.White, modifier = Modifier.padding(top = 28.dp).height(48.dp)) {
            Box(Modifier.padding(horizontal = 32.dp), contentAlignment = Alignment.Center) {
                Text(stringResource(Res.string.suggest_back_to_map), style = bodyStyle(15.sp, FontWeight.SemiBold, Color.White))
            }
        }
    }
}

/** First-run screen; shown only once (see AppViewModel.completeOnboarding). */
@Composable
internal fun AndroidOnboarding(env: AppEnvironment) {
    Column(
        Modifier
            .fillMaxSize()
            .background(VentrovaColors.Canvas)
            .clickable(enabled = false) {}
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(start = 24.dp, end = 24.dp, bottom = 28.dp),
    ) {
        OnboardingIllustration()
        Text(stringResource(Res.string.app_name), style = displayStyle(48, weight = FontWeight.ExtraBold, lineHeight = 0.95f), modifier = Modifier.padding(top = 18.dp))
        Text(stringResource(Res.string.onboarding_tagline), style = bodyStyle(18.sp, color = VentrovaColors.Secondary), modifier = Modifier.padding(top = 10.dp))
        LevelLegend(Modifier.padding(top = 22.dp), fontSize = 15.sp)
        Spacer(Modifier.weight(1f))
        Surface(
            onClick = env::useLocationFromOnboarding,
            enabled = !env.awaitingPermission,
            shape = RoundedCornerShape(28.dp),
            color = VentrovaColors.Accent,
            contentColor = Color.White,
            modifier = Modifier.fillMaxWidth().height(56.dp),
        ) {
            Row(horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
                VIcon(Res.drawable.ic_my_location, size = 22.dp)
                Spacer(Modifier.size(8.dp))
                Text(stringResource(Res.string.onboarding_use_location), style = bodyStyle(16.sp, FontWeight.SemiBold, Color.White))
            }
        }
        Box(Modifier.fillMaxWidth().padding(top = 6.dp).height(48.dp).clickable(onClick = env::skipLocationFromOnboarding), contentAlignment = Alignment.Center) {
            Text(stringResource(Res.string.onboarding_skip), style = bodyStyle(15.sp, FontWeight.SemiBold, VentrovaColors.AccentDark))
        }
    }
}

@Composable
private fun OnboardingIllustration() {
    Box(Modifier.fillMaxWidth().height(290.dp)) {
        Box(Modifier.align(Alignment.TopCenter).offset(y = 40.dp).size(240.dp).rotate(12f).background(VentrovaColors.AccentContainerLight, RoundedCornerShape(72.dp)))
        Box(Modifier.align(Alignment.TopCenter).offset(y = 90.dp).size(140.dp).rotate(-8f).background(VentrovaColors.AccentContainer.copy(alpha = 0.8f), RoundedCornerShape(44.dp)))
        Box(
            Modifier.align(Alignment.TopCenter).offset(y = 151.dp).size(18.dp)
                .background(Color.White, RoundedCornerShape(50))
                .padding(3.dp)
                .background(VentrovaColors.LocationAndroid, RoundedCornerShape(50)),
        )
        IllustrationChip(PlaceCategory.Bakery, VeganLevel.Vegan, Modifier.align(Alignment.TopStart).offset(x = 10.dp, y = 84.dp))
        IllustrationChip(PlaceCategory.Restaurant, VeganLevel.Vegetarian, Modifier.align(Alignment.TopEnd).offset(x = (-12).dp, y = 60.dp))
        IllustrationChip(PlaceCategory.Cafe, VeganLevel.VeganOptions, Modifier.align(Alignment.TopEnd).offset(x = (-46).dp, y = 226.dp))
    }
}

@Composable
private fun IllustrationChip(category: PlaceCategory, level: VeganLevel, modifier: Modifier) {
    Row(
        modifier.height(34.dp).shadow(4.dp, RoundedCornerShape(12.dp)).background(Color.White, RoundedCornerShape(12.dp)).padding(start = 4.dp, end = 11.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Box(Modifier.size(27.dp).background(level.palette.color, RoundedCornerShape(9.dp)), contentAlignment = Alignment.Center) {
            VIcon(category.filledIcon, size = 16.dp, tint = Color.White)
        }
        Text(stringResource(category.label), style = bodyStyle(13.sp, FontWeight.Bold))
    }
}
